import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { X, Search, Tent, MapPin, Plus, Sparkles, ChevronDown, ChevronUp } from '@/ui/icons'
import { POIResult, RoutesApi, DeadEnd as DeadEndType, RangerCampsiteAnalysis, CampingSuitability } from '@/shared/api/routes'
import { cn } from '@/shared/utils'
import { Button, Label, Checkbox, Badge } from '@/ui/components'
import { TripResponse } from '@/shared/api/types'
import { useAuth } from '@/modules/auth'
import { useSubscription } from '@/modules/subscriptions/hooks/useSubscription'

// Use DeadEnd type from routes API
type DeadEnd = DeadEndType

interface POIDiscoveryPanelProps {
  tripId?: string | null
  trip?: TripResponse | null
  poiResults: POIResult[]
  isPOISearching: boolean
  poiSearchError: string | null
  onPOISearch: (params: {
    routeId?: string
    categories: string[]
    maxDistance: number
    legalOnly?: boolean
    centerLat?: number
    centerLng?: number
    tripId?: string
    tripDay?: number
  }) => Promise<void>
  onClearPOIResults: () => void
  onPOIClick: (poi: POIResult) => void
  onAddPOIToTrip: (poi: POIResult) => Promise<void>
  onClose: () => void
  waypoints?: Array<{ latitude?: number | null; longitude?: number | null }>
  userLocation?: { latitude: number; longitude: number } | null
  routeId?: string
  routeName?: string
  // Campsite discovery props
  deadEnds?: DeadEnd[]
  isLoadingDeadEnds?: boolean
  deadEndError?: string | null
  discoverySource?: 'cache' | 'overlapping' | 'queued' | 'live' | null
  discoveryQueuedAt?: string | null
  onFindCampsites?: (selection?: { day?: number; routeId?: string; waypointId?: string }, options?: { silent?: boolean }) => Promise<void>
  onAddDeadEndToTrip?: (deadEnd: DeadEnd) => Promise<void>
  onDeadEndClick?: (deadEnd: DeadEnd) => void
  className?: string
}

const POI_CATEGORIES = [
  { id: 'gas', label: 'Gas Stations' },
  { id: 'water', label: 'Water Sources' },
  { id: 'rest_stop', label: 'Rest Stops' },
  { id: 'viewpoint', label: 'Viewpoints' }
] as const

export function POIDiscoveryPanel({
  tripId,
  trip,
  poiResults,
  isPOISearching,
  poiSearchError,
  onPOISearch,
  onClearPOIResults,
  onPOIClick,
  onAddPOIToTrip,
  onClose,
  waypoints = [],
  userLocation = null,
  routeId,
  routeName,
  deadEnds = [],
  isLoadingDeadEnds = false,
  deadEndError = null,
  discoverySource = null,
  discoveryQueuedAt = null,
  onFindCampsites,
  onAddDeadEndToTrip,
  onDeadEndClick,
  className
}: POIDiscoveryPanelProps) {
  const [activeTab, setActiveTab] = useState<'poi' | 'campsites'>('poi')
  const [selectedCategories, setSelectedCategories] = useState<string[]>(['gas'])
  const [searchRadius, setSearchRadius] = useState([10]) // km
  const [searchMode, setSearchMode] = useState<'nearby' | 'along-route'>(routeId ? 'along-route' : 'nearby')
  const [legalOnly, setLegalOnly] = useState(false)
  const [selectedPOI, setSelectedPOI] = useState<POIResult | null>(null)
  const [isAddingPOI, setIsAddingPOI] = useState(false)
  
  // Ranger analysis state
  const [analyzingDeadEnds, setAnalyzingDeadEnds] = useState(false)
  const [rangerAnalyses, setRangerAnalyses] = useState<Map<string, RangerCampsiteAnalysis>>(new Map())
  const [expandedDeadEnds, setExpandedDeadEnds] = useState<Set<string>>(new Set())
  
  // Get auth and subscription
  const { getAccessToken } = useAuth()
  const { status: subscriptionStatus, canUseRanger } = useSubscription()
  
  // Initialize RoutesApi
  const routesApi = useMemo(() => new RoutesApi(getAccessToken), [getAccessToken])

  // Poll for results when area has been queued (scan in progress). Stops when data arrives or after 5 min.
  const pollCountRef = useRef(0)
  const POLL_INTERVAL_MS = 45_000
  const MAX_POLLS = 7 // ~5 minutes
  useEffect(() => {
    if (discoverySource !== 'queued' || deadEnds.length > 0 || !onFindCampsites) return
    pollCountRef.current = 0
    const id = setInterval(() => {
      pollCountRef.current += 1
      if (pollCountRef.current > MAX_POLLS) return
      onFindCampsites(undefined, { silent: true })
    }, POLL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [discoverySource, deadEnds.length, onFindCampsites])

  // Calculate center point from waypoints if available
  const calculateWaypointCenter = useCallback((): { lat: number; lng: number } | null => {
    const validWaypoints = waypoints.filter(
      wp => wp.latitude != null && wp.longitude != null && 
            !isNaN(wp.latitude!) && !isNaN(wp.longitude!)
    )
    
    if (validWaypoints.length === 0) {
      return null
    }
    
    const avgLat = validWaypoints.reduce((sum, wp) => sum + (wp.latitude || 0), 0) / validWaypoints.length
    const avgLng = validWaypoints.reduce((sum, wp) => sum + (wp.longitude || 0), 0) / validWaypoints.length
    
    return { lat: avgLat, lng: avgLng }
  }, [waypoints])

  const handleSearch = useCallback(async () => {
    if (selectedCategories.length === 0) {
      return
    }

    if (searchMode === 'along-route' && !routeId) {
      return
    }

    if (searchMode === 'nearby') {
      const waypointCenter = calculateWaypointCenter()
      const center = waypointCenter || userLocation
      
      if (!center) {
        return
      }
      
      try {
        // Normalize coordinates - handle both { lat, lng } and { latitude, longitude } formats
        const centerLat = 'lat' in center ? center.lat : center.latitude;
        const centerLng = 'lng' in center ? center.lng : center.longitude;
        
        await onPOISearch({
          categories: selectedCategories,
          maxDistance: searchRadius[0] * 1000,
          legalOnly: selectedCategories.includes('water') ? legalOnly : undefined,
          centerLat,
          centerLng,
          tripId: tripId || undefined,
        })
      } catch (error) {
      }
    } else if (searchMode === 'along-route' && routeId) {
      try {
        await onPOISearch({
          routeId,
          categories: selectedCategories,
          maxDistance: searchRadius[0] * 1000,
          legalOnly: selectedCategories.includes('water') ? legalOnly : undefined,
          tripId: tripId || undefined,
        })
      } catch (error) {
      }
    }
  }, [selectedCategories, searchRadius, searchMode, routeId, onPOISearch, legalOnly, calculateWaypointCenter, userLocation, tripId])

  const handlePOIItemClick = useCallback((poi: POIResult) => {
    setSelectedPOI(poi)
    onPOIClick(poi)
  }, [onPOIClick])

  const handleAddPOI = useCallback(async (poi: POIResult) => {
    setIsAddingPOI(true)
    try {
      await onAddPOIToTrip(poi)
      setSelectedPOI(null)
    } catch (error) {
    } finally {
      setIsAddingPOI(false)
    }
  }, [onAddPOIToTrip])

  const formatDistance = (distance?: number) => {
    if (!distance) return ''
    if (distance < 1000) return `${Math.round(distance)}m`
    return `${(distance / 1000).toFixed(1)}km`
  }

  // Helper: Generate candidate ID matching backend format
  const getCandidateId = useCallback((deadEnd: DeadEnd): string => {
    const [lng, lat] = deadEnd.coordinates
    return `deadend_${lng.toFixed(6)}_${lat.toFixed(6)}`.replace(/[.-]/g, '_')
  }, [])

  // Helper: Get suitability badge variant
  const getSuitabilityVariant = useCallback((suitability: CampingSuitability): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (suitability) {
      case 'excellent':
        return 'default'
      case 'good':
        return 'default'
      case 'fair':
        return 'secondary'
      case 'poor':
        return 'outline'
      case 'not_recommended':
        return 'destructive'
      default:
        return 'secondary'
    }
  }, [])

  // Helper: Format suitability label
  const formatSuitabilityLabel = useCallback((suitability: CampingSuitability): string => {
    switch (suitability) {
      case 'excellent':
        return 'Excellent'
      case 'good':
        return 'Good'
      case 'fair':
        return 'Fair'
      case 'poor':
        return 'Poor'
      case 'not_recommended':
        return 'Not Recommended'
      default:
        return suitability
    }
  }, [])

  // Handler: Analyze dead ends with Ranger AI
  const handleAnalyzeWithRanger = useCallback(async () => {
    if (deadEnds.length === 0 || !tripId) return
    
    setAnalyzingDeadEnds(true)
    try {
      const result = await routesApi.analyzeDeadEnds(deadEnds, {
        tripId,
        maxAnalyze: 10 // Limit to top 10 for cost control
      })
      
      // Map analyses by candidateId for easy lookup
      const analysesMap = new Map(
        result.data.analyses.map(analysis => [analysis.candidateId, analysis])
      )
      setRangerAnalyses(analysesMap)

      if (result.data.skippedCount > 0) {
        // Skipped count shown in UI; toast deprecated
      }
    } catch (error) {

    } finally {
      setAnalyzingDeadEnds(false)
    }
  }, [deadEnds, tripId, routesApi])

  // Toggle expanded state for dead end details
  const toggleDeadEndExpanded = useCallback((candidateId: string) => {
    setExpandedDeadEnds(prev => {
      const next = new Set(prev)
      if (next.has(candidateId)) {
        next.delete(candidateId)
      } else {
        next.add(candidateId)
      }
      return next
    })
  }, [])

  return (
    <div
      className={cn(
        'fixed right-0 top-20 h-[calc(100vh-5rem)] w-64 bg-white border-l border-gray-200 shadow-lg z-[9999] flex flex-col transition-transform duration-300 ease-in-out pointer-events-auto',
        className
      )}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between p-4 border-b border-gray-200 pointer-events-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          <Search className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold text-gray-900">
            Discover Places
          </h2>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onClose()
          }}
          className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors pointer-events-auto relative z-[9999]"
          aria-label="Close panel"
          type="button"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('poi')}
          className={cn(
            'flex-1 px-4 py-2 text-sm font-medium transition-colors',
            activeTab === 'poi'
              ? 'text-primary border-b-2 border-primary bg-adventure-50'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          )}
        >
          <div className="flex items-center justify-center gap-2">
            <Search className="w-4 h-4" />
            POI Search
          </div>
        </button>
        <button
          onClick={() => setActiveTab('campsites')}
          className={cn(
            'flex-1 px-4 py-2 text-sm font-medium transition-colors',
            activeTab === 'campsites'
              ? 'text-primary border-b-2 border-primary bg-earth-50'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          )}
        >
          <div className="flex items-center justify-center gap-2">
            <Tent className="w-4 h-4" />
            Campsites
          </div>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 pointer-events-auto">
        {activeTab === 'poi' ? (
          <div className="space-y-4">
            {/* Search Mode */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Search Mode</Label>
              <div className="flex gap-2">
                <Button
                  variant={searchMode === 'along-route' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSearchMode('along-route')}
                  disabled={!routeId}
                  className="flex-1"
                >
                  Along Route
                </Button>
                <Button
                  variant={searchMode === 'nearby' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSearchMode('nearby')}
                  disabled={!calculateWaypointCenter() && !userLocation}
                  className="flex-1"
                >
                  Nearby
                </Button>
              </div>
            </div>

            {/* Categories */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Categories</Label>
              <div className="grid grid-cols-1 gap-2">
                {POI_CATEGORIES.map(category => {
                  const isSelected = selectedCategories.includes(category.id)
                  return (
                    <label
                      key={category.id}
                      htmlFor={category.id}
                      className={cn(
                        'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-200',
                        isSelected
                          ? 'bg-adventure-50 border-adventure-300 shadow-sm'
                          : 'bg-white border-earth-200 hover:bg-earth-50 hover:border-earth-300'
                      )}
                    >
                      <Checkbox
                        id={category.id}
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCategories(prev => [...prev, category.id])
                          } else {
                            setSelectedCategories(prev => prev.filter(id => id !== category.id))
                          }
                        }}
                        className={cn(
                          'h-4 w-4',
                          isSelected && 'border-adventure-600'
                        )}
                      />
                      <span className={cn(
                        'text-sm font-medium flex-1',
                        isSelected ? 'text-adventure-900' : 'text-earth-700'
                      )}>
                        {category.label}
                      </span>
                    </label>
                  )
                })}
              </div>
            </div>

            {/* Legal Drinking Water Filter */}
            {selectedCategories.includes('water') && (
              <div className="space-y-2">
                <label
                  htmlFor="legal-only"
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-200',
                    legalOnly
                      ? 'bg-adventure-50 border-adventure-300 shadow-sm'
                      : 'bg-white border-earth-200 hover:bg-earth-50 hover:border-earth-300'
                  )}
                >
                  <Checkbox
                    id="legal-only"
                    checked={legalOnly}
                    onChange={(e) => setLegalOnly(e.target.checked)}
                    className={cn(
                      'h-4 w-4',
                      legalOnly && 'border-adventure-600'
                    )}
                  />
                  <span className={cn(
                    'text-sm font-medium flex-1',
                    legalOnly ? 'text-adventure-900' : 'text-earth-700'
                  )}>
                    Legal drinking water only
                  </span>
                </label>
              </div>
            )}

            {/* Search Radius */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Search Radius: {searchRadius[0]}km
              </Label>
              <input
                type="range"
                min={1}
                max={50}
                step={1}
                value={searchRadius[0]}
                onChange={(e) => setSearchRadius([parseInt(e.target.value)])}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            {/* Search Button */}
            <Button 
              onClick={handleSearch} 
              disabled={isPOISearching || selectedCategories.length === 0}
              className="w-full"
            >
              {isPOISearching ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Search Places
                </>
              )}
            </Button>

            {/* Error Message */}
            {poiSearchError && (
              <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                {poiSearchError}
              </div>
            )}

            {/* Results */}
            {poiResults.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">
                    Found {poiResults.length} places
                  </Label>
                  <Button variant="ghost" size="sm" onClick={onClearPOIResults}>
                    Clear
                  </Button>
                </div>
                
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {poiResults.map(poi => (
                    <div
                      key={poi.id}
                      className={cn(
                        'p-3 border rounded-lg transition-colors',
                        selectedPOI?.id === poi.id
                          ? 'bg-adventure-50 border-adventure-300'
                          : 'hover:bg-gray-50 cursor-pointer'
                      )}
                      onClick={() => handlePOIItemClick(poi)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm truncate">{poi.name}</h4>
                          <p className="text-xs text-muted-foreground truncate mt-1">
                            {poi.address || `${poi.latitude.toFixed(4)}, ${poi.longitude.toFixed(4)}`}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="secondary" className="text-xs">
                              {poi.category}
                            </Badge>
                            {poi.distance && (
                              <span className="text-xs text-muted-foreground">
                                {formatDistance(poi.distance)}
                              </span>
                            )}
                          </div>
                        </div>
                        <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0 ml-2" />
                      </div>
                      {selectedPOI?.id === poi.id && (
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleAddPOI(poi)
                          }}
                          disabled={isAddingPOI || !tripId}
                          className="w-full mt-2"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          {isAddingPOI ? 'Adding...' : 'Add to Trip'}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {onFindCampsites ? (
              <>
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Discover potential campsites by finding dead ends near your route waypoints.
                  </p>
                  {deadEndError && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/50 p-3 text-sm text-amber-800 dark:text-amber-200">
                      <p className="mb-2">{deadEndError}</p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => onFindCampsites()}
                        disabled={isLoadingDeadEnds}
                      >
                        Retry
                      </Button>
                    </div>
                  )}
                  {discoverySource === 'queued' && deadEnds.length === 0 && (
                    <div className="space-y-2 rounded-lg border border-earth-200 dark:border-earth-700 bg-earth-50 dark:bg-earth-900/30 p-3">
                      <p className="text-sm font-medium text-earth-900 dark:text-earth-100">
                        Scan requested
                      </p>
                      <p className="text-xs text-muted-foreground">
                        We&apos;re scanning this area for forest roads and potential campsites. This usually takes a few minutes. We&apos;ll check automatically—you can also click &quot;Check for results&quot; below or keep exploring the map.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onFindCampsites?.(undefined, { silent: true })}
                        disabled={isLoadingDeadEnds || !tripId}
                      >
                        Check for results
                      </Button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      onClick={() => onFindCampsites?.()}
                      disabled={isLoadingDeadEnds || !tripId}
                      variant={isLoadingDeadEnds ? 'outline' : 'default'}
                      className="flex-1"
                    >
                      {isLoadingDeadEnds ? (
                        <>
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                          <span className="text-foreground">Checking area...</span>
                        </>
                      ) : (
                        <>
                          <Tent className="w-4 h-4 mr-2" />
                          Find Campsites
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Analyze with Ranger AI button */}
                  {deadEnds.length > 0 && canUseRanger && (
                    <Button
                      onClick={handleAnalyzeWithRanger}
                      disabled={analyzingDeadEnds || !tripId}
                      variant="outline"
                      className="w-full"
                    >
                      {analyzingDeadEnds ? (
                        <>
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Analyze with Ranger AI
                        </>
                      )}
                    </Button>
                  )}
                  
                  {/* Upgrade prompt if no access */}
                  {deadEnds.length > 0 && !canUseRanger && (
                    <div className="text-xs text-muted-foreground bg-adventure-50 p-2 rounded border border-adventure-200">
                      <p className="font-medium mb-1">Ranger AI Analysis</p>
                      <p>Upgrade to Pro to get AI-powered campsite analysis with safety recommendations, vehicle requirements, and seasonal considerations.</p>
                    </div>
                  )}
                </div>

                {deadEnds.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Found {deadEnds.length} potential campsites
                    </Label>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {deadEnds.map((deadEnd, index) => {
                        const candidate = deadEnd.candidate
                        const candidateId = getCandidateId(deadEnd)
                        const analysis = rangerAnalyses.get(candidateId)
                        const isExpanded = expandedDeadEnds.has(candidateId)
                        
                        return (
                          <div
                            key={`${deadEnd.roadId}-${index}`}
                            className="p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <div 
                              className="cursor-pointer"
                              onClick={() => {
                                // Center map on dead end
                                if (onDeadEndClick) {
                                  onDeadEndClick(deadEnd)
                                }
                              }}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h4 className="font-medium text-sm">
                                      {deadEnd.roadName || 'Unnamed Road'}
                                    </h4>
                                    {analysis && (
                                      <Badge 
                                        variant={getSuitabilityVariant(analysis.analysis.campingSuitability)}
                                        className="text-xs"
                                      >
                                        {formatSuitabilityLabel(analysis.analysis.campingSuitability)} ({analysis.suitabilityScore}/100)
                                      </Badge>
                                    )}
                                  </div>
                                  {deadEnd.distanceFromMainRoad && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {formatDistance(deadEnd.distanceFromMainRoad)} from main road
                                    </p>
                                  )}
                                  
                                  {/* Ranger AI Analysis Summary */}
                                  {analysis && (
                                    <div className="mt-2 space-y-2 border-t pt-2">
                                      <p className="text-xs text-muted-foreground">
                                        {analysis.analysis.summary}
                                      </p>
                                      
                                      {/* Expandable Details */}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          toggleDeadEndExpanded(candidateId)
                                        }}
                                        className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                                      >
                                        {isExpanded ? (
                                          <>
                                            <ChevronUp className="w-3 h-3" />
                                            Hide Details
                                          </>
                                        ) : (
                                          <>
                                            <ChevronDown className="w-3 h-3" />
                                            View Details
                                          </>
                                        )}
                                      </button>
                                      
                                      {isExpanded && (
                                        <div className="space-y-2 pt-2 text-xs">
                                          {/* Strengths */}
                                          {analysis.analysis.strengths.length > 0 && (
                                            <div>
                                              <p className="font-medium text-green-700 mb-1">Strengths:</p>
                                              <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                                                {analysis.analysis.strengths.map((strength, idx) => (
                                                  <li key={idx}>{strength}</li>
                                                ))}
                                              </ul>
                                            </div>
                                          )}
                                          
                                          {/* Concerns */}
                                          {analysis.analysis.concerns.length > 0 && (
                                            <div>
                                              <p className="font-medium text-orange-700 mb-1">Concerns:</p>
                                              <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                                                {analysis.analysis.concerns.map((concern, idx) => (
                                                  <li key={idx}>{concern}</li>
                                                ))}
                                              </ul>
                                            </div>
                                          )}
                                          
                                          {/* Recommendations */}
                                          {analysis.analysis.recommendations.length > 0 && (
                                            <div>
                                              <p className="font-medium text-primary mb-1">Recommendations:</p>
                                              <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                                                {analysis.analysis.recommendations.map((rec, idx) => (
                                                  <li key={idx}>{rec}</li>
                                                ))}
                                              </ul>
                                            </div>
                                          )}
                                          
                                          {/* Vehicle Requirements */}
                                          {analysis.analysis.vehicleRequirements && analysis.analysis.vehicleRequirements.length > 0 && (
                                            <div>
                                              <p className="font-medium mb-1">Vehicle Requirements:</p>
                                              <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                                                {analysis.analysis.vehicleRequirements.map((req, idx) => (
                                                  <li key={idx}>{req}</li>
                                                ))}
                                              </ul>
                                            </div>
                                          )}
                                          
                                          {/* Seasonal Considerations */}
                                          {analysis.analysis.seasonalConsiderations && (
                                            <div>
                                              <p className="font-medium mb-1">Seasonal:</p>
                                              <p className="text-muted-foreground">{analysis.analysis.seasonalConsiderations}</p>
                                            </div>
                                          )}
                                          
                                          {/* Safety Warnings */}
                                          {analysis.analysis.safetyWarnings && analysis.analysis.safetyWarnings.length > 0 && (
                                            <div>
                                              <p className="font-medium text-red-700 mb-1">Safety Warnings:</p>
                                              <ul className="list-disc list-inside space-y-0.5 text-red-600">
                                                {analysis.analysis.safetyWarnings.map((warning, idx) => (
                                                  <li key={idx}>{warning}</li>
                                                ))}
                                              </ul>
                                            </div>
                                          )}
                                          
                                          {/* Permits Required */}
                                          {analysis.analysis.permitsRequired && analysis.analysis.permitsRequired.length > 0 && (
                                            <div>
                                              <p className="font-medium mb-1">Permits:</p>
                                              <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                                                {analysis.analysis.permitsRequired.map((permit, idx) => (
                                                  <li key={idx}>{permit}</li>
                                                ))}
                                              </ul>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Display additional candidate data if available */}
                                  {candidate && !analysis && (
                                    <div className="mt-2 space-y-1">
                                      {/* Score */}
                                      {candidate.totalScore !== undefined && (
                                        <div className="flex items-center gap-1">
                                          <span className="text-xs font-medium text-primary">Score:</span>
                                          <span className="text-xs text-muted-foreground">{Math.round(candidate.totalScore)}/100</span>
                                        </div>
                                      )}
                                      
                                      {/* Water Body */}
                                      {candidate.waterBody && (
                                        <div className="text-xs text-muted-foreground">
                                          💧 Water: {candidate.waterBody.name || candidate.waterBody.type} ({formatDistance(candidate.waterBody.distanceMeters)})
                                        </div>
                                      )}
                                      
                                      {/* Land Ownership */}
                                      {candidate.landOwnership && (
                                        <div className="text-xs text-muted-foreground">
                                          🏞️ {candidate.landOwnership.designation}
                                          {candidate.landOwnership.unitName && ` - ${candidate.landOwnership.unitName}`}
                                          {candidate.landOwnership.campingAllowed !== undefined && (
                                            <span className={candidate.landOwnership.campingAllowed ? ' text-green-600' : ' text-orange-600'}>
                                              {' '}({candidate.landOwnership.campingAllowed ? 'Camping allowed' : 'Check restrictions'})
                                            </span>
                                          )}
                                        </div>
                                      )}
                                      
                                      {/* MVUM Status */}
                                      {candidate.mvumStatus && (
                                        <div className="text-xs text-muted-foreground">
                                          🛣️ MVUM: {candidate.mvumStatus.status === 'open' ? '✅ Open' : candidate.mvumStatus.status === 'restricted' ? '⚠️ Restricted' : candidate.mvumStatus.status === 'closed' ? '❌ Closed' : candidate.mvumStatus.status}
                                        </div>
                                      )}
                                      
                                      {/* Terrain */}
                                      {candidate.terrain && (
                                        <div className="text-xs text-muted-foreground">
                                          {candidate.terrain.meanElevation !== null && `⛰️ Elevation: ${Math.round(candidate.terrain.meanElevation)}m`}
                                          {candidate.terrain.slopeDegrees !== null && candidate.terrain.slopeDegrees > 0 && ` • Slope: ${candidate.terrain.slopeDegrees.toFixed(1)}°`}
                                        </div>
                                      )}
                                      
                                      {/* Surface Type */}
                                      {candidate.surface && (
                                        <div className="text-xs text-muted-foreground">
                                          🛤️ Surface: {candidate.surface}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <Tent className="w-4 h-4 text-earth-600 flex-shrink-0 ml-2" />
                              </div>
                            </div>
                            {onAddDeadEndToTrip && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  onAddDeadEndToTrip(deadEnd)
                                }}
                                disabled={!tripId}
                                className="w-full mt-2"
                              >
                                <Plus className="w-4 h-4 mr-2" />
                                Add to Trip
                              </Button>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-sm text-muted-foreground text-center py-8">
                Campsite discovery not available
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

